import { useState } from "react";
import { Upload, Eye, Trash2, Users, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Field, TextInput } from "@/components/common/Forms";
import { FileThumb, FilePicker, FilePreviewDialog, DownloadButton } from "@/components/common/Files";
import { EmptyState, Loader, PageHeader } from "@/components/common/Loader";
import { api, errMsg, useFetch } from "@/lib/api";
import { SECTIONS, fmtDate } from "@/lib/format";

const EMPTY = { section: "documenti", title: "", all: false, types: [] };

function Audience({ f, setF, types }) {
  const toggle = (id) => setF({ ...f, types: f.types.includes(id) ? f.types.filter((x) => x !== id) : [...f.types, id] });
  const Row = ({ id, label, checked, onChange }) => (
    <label className="flex items-center gap-3 py-2 cursor-pointer" data-testid={`audience-${id}`}>
      <Checkbox checked={checked} onCheckedChange={onChange} className="border-gold data-[state=checked]:bg-gold data-[state=checked]:text-black" />
      <span className="text-sm">{label}</span>
    </label>
  );
  return (
    <div className="rounded-xl border border-white/10 px-4 py-2">
      <Row id="all" label="Tutti i centri" checked={f.all} onChange={(v) => setF({ ...f, all: !!v, types: [] })} />
      <div className={`border-t border-white/10 pt-1 ${f.all ? "opacity-40 pointer-events-none" : ""}`}>
        {types.map((t) => <Row key={t.id} id={t.id} label={t.name} checked={f.types.includes(t.id)} onChange={() => toggle(t.id)} />)}
      </div>
    </div>
  );
}

function UploadDialog({ open, onOpenChange, types, onUploaded }) {
  const [f, setF] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!files.length) return toast.error("Seleziona almeno un file");
    if (!f.all && !f.types.length) return toast.error("Scegli per chi è il documento");
    setBusy(true);
    const fd = new FormData();
    fd.append("section", f.section);
    fd.append("title", f.title);
    fd.append("audience_all", f.all ? "true" : "false");
    fd.append("client_type_ids", f.types.join(","));
    files.forEach((x) => fd.append("files", x));
    try {
      const r = await api.post("/admin/shared-documents", fd);
      const n = r.data.centers_notified;
      toast.success(n === 1 ? "Documento inviato: 1 centro riceverà un'email" : `Documento inviato: ${n} centri riceveranno un'email`);
      setF(EMPTY);
      setFiles([]);
      onOpenChange(false);
      onUploaded();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ivory-card rounded-xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="shared-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal">Documento per più centri</DialogTitle>
          <DialogDescription>Lo carichi una volta e lo vedono tutti i centri che scegli, anche quelli che arriveranno dopo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <Field label="Sezione">
            <div className="flex gap-2">
              {["documenti", "loghi"].map((k) => (
                <button key={k} type="button" data-testid={`shared-section-${k}`} onClick={() => setF({ ...f, section: k })}
                  className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.14em] border transition-colors ${f.section === k ? "border-gold bg-gold/15 text-gold-light" : "border-white/15 text-warm-text"}`}>{SECTIONS[k]}</button>
              ))}
            </div>
          </Field>
          <Field label="Titolo (facoltativo)"><TextInput data-testid="shared-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Es. Listino prezzi 2026" /></Field>
          <Field label="Per chi è il documento? *"><Audience f={f} setF={setF} types={types} /></Field>
          <FilePicker files={files} setFiles={setFiles} accept=".pdf,.png,.jpg,.jpeg,.svg,.webp" testid="shared-files" label="Scegli i file" />
          <button data-testid="shared-upload-submit-btn" disabled={busy} onClick={submit} className="btn-gold w-full">{busy ? "Invio…" : "Carica e invia"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Readers({ doc, onClose }) {
  const { data, loading } = useFetch(doc ? `/admin/shared-documents/${doc.id}/reads` : null);
  const [tab, setTab] = useState("all");
  const rows = (data || []).filter((r) => tab === "all" || (tab === "read" ? r.first_at : !r.first_at));
  const read = (data || []).filter((r) => r.first_at).length;
  const tabs = [["all", `Tutti · ${data?.length || 0}`], ["read", `Aperto · ${read}`], ["unread", `Non ancora · ${(data?.length || 0) - read}`]];
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-ivory-card rounded-xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="shared-reads-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal">Chi l'ha visto</DialogTitle>
          <DialogDescription>{doc?.title}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {tabs.map(([k, l]) => (
            <button key={k} data-testid={`reads-tab-${k}`} onClick={() => setTab(k)}
              className={`rounded-full px-3 py-1 text-xs border transition-colors ${tab === k ? "border-gold bg-gold/15 text-gold-light" : "border-white/15 text-warm-text"}`}>{l}</button>
          ))}
        </div>
        {loading || !data ? <Loader /> : !rows.length ? <p className="text-sm text-warm-text py-4">Nessun centro in questo elenco.</p> : (
          <div className="divide-y divide-warm/30" data-testid="reads-list">
            {rows.map((r) => (
              <div key={r.center_id} data-testid={`read-row-${r.center_id}`} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm truncate">{r.center_name}</p>
                  <p className="text-xs text-warm-text">{r.city}</p>
                </div>
                {r.first_at ? (
                  <div className="text-right shrink-0">
                    <p className="text-xs text-gold-light inline-flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" /> {r.downloads ? "Scaricato" : "Aperto"}</p>
                    <p className="text-[11px] text-warm-text">il {fmtDate(r.first_at)}{r.opens + r.downloads > 1 ? ` · ${r.opens + r.downloads} volte` : ""}</p>
                  </div>
                ) : <span className="text-xs text-warm-text shrink-0">Non ancora</span>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SharedRow({ d, onPreview, onDelete, onReads }) {
  const who = d.audience_all ? "Tutti i centri" : (d.audience_names || []).join(", ");
  return (
    <div data-testid={`shared-doc-${d.id}`} className="flex items-center gap-4 py-4">
      <button onClick={() => onPreview(d)}><FileThumb kind="document" file={d} className="h-12 w-12" /></button>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">{d.title} <span className="text-[10px] uppercase tracking-[0.14em] text-warm-text ml-1">{SECTIONS[d.section]}</span></p>
        <p className="text-xs text-gold-light truncate inline-flex items-center gap-1.5"><Users className="w-3 h-3" /> {who}</p>
        <p className="text-xs text-warm-text truncate">Caricato il {fmtDate(d.created_at)} da {d.uploaded_by_name}</p>
        <button data-testid={`shared-doc-reads-${d.id}`} onClick={() => onReads(d)} className="mt-1 text-xs text-gold-light underline underline-offset-4 inline-flex items-center gap-1">
          <CheckCheck className="w-3.5 h-3.5" /> Visto da {d.read_count} su {d.audience_count} centri
        </button>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button data-testid={`shared-doc-preview-${d.id}`} onClick={() => onPreview(d)} aria-label="Anteprima"><Eye className="w-4 h-4" strokeWidth={1.4} /></button>
        <DownloadButton kind="document" id={d.id} compact label="" testid={`shared-doc-download-${d.id}`} />
        <button data-testid={`shared-doc-delete-${d.id}`} onClick={() => onDelete(d)} aria-label="Elimina"><Trash2 className="w-4 h-4 text-destructive" strokeWidth={1.4} /></button>
      </div>
    </div>
  );
}

export default function SharedDocuments() {
  const { data, loading, reload } = useFetch("/admin/shared-documents");
  const { data: lists } = useFetch("/admin/lists");
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reads, setReads] = useState(null);
  const del = async (d) => {
    if (!window.confirm(`Eliminare "${d.title}"? Sparirà per tutti i centri.`)) return;
    try {
      await api.delete(`/admin/documents/${d.id}`);
      toast.success("Documento eliminato");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const types = (lists?.["client-types"] || []).filter((t) => t.active);
  return (
    <div>
      <PageHeader eyebrow="Documenti" title="Documenti per più centri" subtitle="Carica un documento una sola volta e scegli per chi è: tutti i centri o solo alcune tipologie." />
      <div className="flex justify-end mb-6">
        <button data-testid="open-shared-upload-btn" onClick={() => setOpen(true)} className="btn-gold"><Upload className="w-4 h-4" /> Carica documento</button>
      </div>
      {loading && !data ? <Loader /> : !data?.length ? <EmptyState title="Nessun documento condiviso" text="I documenti che carichi qui compaiono in automatico nell'area dei centri scelti." /> : (
        <div className="lux-card px-6 py-2 divide-y divide-warm/30" data-testid="shared-doc-list">
          {data.map((d) => <SharedRow key={d.id} d={d} onPreview={setPreview} onDelete={del} onReads={setReads} />)}
        </div>
      )}
      <UploadDialog open={open} onOpenChange={setOpen} types={types} onUploaded={reload} />
      <FilePreviewDialog kind="document" file={preview} onClose={() => setPreview(null)} />
      <Readers key={reads?.id || "none"} doc={reads} onClose={() => setReads(null)} />
    </div>
  );
}
